import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import UserActivityListener from './util/UserActivityListener'
import PlayerControls from './controls/PlayerControls'
import throttle from 'lodash.throttle'

import './Player.css'

export default class Player extends React.PureComponent {

	static VOLUME = 0.75

	constructor(props) {

		super(props)

		this.state = {
			isInitiated: false,
			isPlaying: false,
			videoProps: {
				volume: Player.VOLUME,
				startTime: props.startTime,
				muted: props.muted,
				progress: 0,
				buffered: 0,
			}
		}

		this.handleSeek = this.handleSeek.bind(this)
		this.handleVideoClick = this.handleVideoClick.bind(this)
		this.handleVideoDoubleClick = this.handleVideoDoubleClick.bind(this)
		this.handleFullscreenClick = this.handleFullscreenClick.bind(this)
		this.handleIdleChange = this.handleIdleChange.bind(this)

		this.onKeyDown = this.onKeyDown.bind(this)

		const videoListenerNames = [
			'onLoadedMetadata',
			'onCanPlay',
			'onSeeked',
			'onPlaying',
			'onTimeUpdate',
			'onProgress',
			'onEnded',
			'onError',
			'onWaiting'
		]

		this.videoListeners = []

		videoListenerNames.forEach( name => {
			this.videoListeners[name] = this[name].bind(this)
		})

		this.handleVolumeUpdate = this.handleVolumeUpdate.bind(this)
		this.setVideoTimeThrottled = throttle(time => {
			this.video.currentTime = time
		}, 250)

	}

	// -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  -  - React Lifecycle

	componentDidMount() {

		// listen for key events (space bar mainly)
		document.addEventListener('keydown', this.onKeyDown)

		// start playback automatically if indicated
		if (this.props.autoPlay && !this.hasAutoPlayed) {
			this.hasAutoPlayed = true
			this.play({ silent: true })
		}

		const volume = Player.VOLUME = Player.VOLUME || 0.75
		this.video.volume = volume

	}

	componentDidUpdate(prevProps) {
		const { willPlay: nextWillPlay } = this.props 
		const { willPlay: prevWillPlay } = prevProps
		const willPlayChanged = nextWillPlay !== prevWillPlay

		if (willPlayChanged) {
			if (nextWillPlay) {
				this.play()
			} else {
				this.pause()
			}
		}
		
		if (prevProps.active === true && this.props.active === false) {
			this.pause()
		}
	}

	componentWillUnmount() {
		// clean up
		clearTimeout(this.buttonAnimationTimeout)
		document.removeEventListener('keydown', this.onKeyDown)


	}

	componentWillReceiveProps(props) {

		if (props.data !== this.props.data) {
			this.resetState()
		}

	}

	resetState() {
		const props = this.props

		this.setState({
			isInitiated: false,
			isPlaying: false,
			videoProps: {
				volume: Player.VOLUME,
				startTime: props.startTime,
				muted: props.muted
			}
		})

	}

	preload() {

		if ( !this.state.videoProps.hasMetaData ) {
			this.video.load()
		}

	}

	togglePlay() {

		if ( this.video.paused ) {
			this.play()
		}
		else {
			this.pause()
		}

	}

	play() {

		if ( !this.state.isInitiated ) {
			this.video.load()
		}

		// play video and keep track of its playing-promise to avoid interruption
		this.playPromise = this.video.play()
		// only store promise if it's actually a Promise
		this.playPromise = false
		var isPlayPromise = this.playPromise && (typeof(this.playPromise) === 'object' && this.playPromise.then)

		if ( isPlayPromise ) {
			this.playPromise.catch(e => console.log(e));
		}

		// set state and animate button
		this.setState({
			isInitiated: true,
			isPlaying: true,
		})

	}

	handleIdleChange(idle) {
		this.props.onIdle && this.props.onIdle(idle)
	}

	handleVolumeUpdate(volume) {
		Player.VOLUME = volume

		this.setState(state => ({
			videoProps: {
				...state.videoProps, volume
			}
		}), () => {
			this.video.volume = this.state.videoProps.volume
		})
	}

	pause() {

		if ( !this.playPromise ) {
			this.video.pause()
		} else {
			this.playPromise.then(() => {
				this.video.pause()
			})
		}

		this.setState({
			isPlaying: false,
		})

	}

	rewind() {

		if (!this.video) { return }

		this.video.pause()
		this.video.currentTime = 0
		this.video.load()

		this.setState({
			isInitiated: false,
			isPlaying: false
		})

	}

	handleVideoClick() {

		this.togglePlay()

	}

	onSeeked() {
		this.setState({
			isBuffering: false
		})
	}

	handleSeek({ time = 0, scrubbing = false } = {}) {
		if ( this.video ) {
			this.setVideoTimeThrottled(time)
			const progress = time / this.video.duration

			this.setState(state => ({
				scrubbing,
				progress,
				time,
				isBuffering: !scrubbing,
				videoProps: {
					...this.state.videoProps,
					progress,
					time
				}
			}))
		}

	}

	handleVideoDoubleClick() {

		this.props.toggleFullscreen();

	}

	handleFullscreenClick() {

		this.props.toggleFullscreen();

	}

	onKeyDown(e) {

		if ( e.keyCode === 32) {
			const rect = this.video.getBoundingClientRect()
			if (rect.bottom < 0 || rect.top > window.innerHeight) { return }
			e.preventDefault()
			e.stopPropagation()
			this.togglePlay()
		}

	}

	onLoadedMetadata( event ) {

		const video = event.target
		const { startTime } = this.state.videoProps

		if ( !this.state.hasMetaData && this.state.videoProps.startTime ) {
			// in order to set currentTime (default start time in this case)
			// the metadata needs to be present on most browsers
			// video.currentTime = this.state.videoProps.startTime
		}

		this.setState(state => ({
			videoProps: {
				...state.videoProps,
				hasMetaData: true,
				time: startTime,
				startTime: 0, /* reset */
				progress: 0,
				buffered: 0,
				duration: video.duration
			}
		}))
	}

	onTimeUpdate( event ) {
		if (this.state.scrubbing) { return }
		const video = event.target
		const progress = video.currentTime / this.video.duration

		this.setState(state => ({
			videoProps: {
				...state.videoProps,
				progress,
				time: video.currentTime
			}
		}))

	}

	onEnded(e) {

		this.rewind()
		this.props.onEnded && this.props.onEnded()

	}

	onProgress(e) {

		let len = this.video.buffered.length

		if ( !len ) { return }

		let bufferedTime = this.video.buffered.end(len - 1)
		let duration = this.video.duration || 0
		let buffered = bufferedTime / duration

		if (duration > 0 && buffered > 0 && buffered <= 1) {
			this.setState( state => ({
				videoProps: {
					...state.videoProps,
					buffered
				}
			}))
		}

	}

	onError(e) {
		console.warn(e);
	}

	onPlaying(e) {

		this.setState({
			isBuffering: false
		}, () => {
			if ( this.props.onPlaying ) {
				this.props.onPlaying(e)
			}
		})

	}

	onCanPlay(e) {
		this.setState({
			isBuffering: false
		})
		if ( this.props.onCanPlay ) {
			this.props.onCanPlay(e)
		}

	}

	onWaiting(e) {

		this.setState({
			isBuffering: true
		})

	}

	render() {

		var style = this.props.style

		var classes = classnames('video-player', 'player', {
			'player--initiated': this.state.isInitiated,
			'player--playing': this.state.isPlaying,
			'player--paused': !this.state.isPlaying,
			'player--buffering': this.state.isBuffering,
			'player--metadata': this.state.videoProps.hasMetaData,
			'player--fullscreen-available': this.props.fullscreenAvailable,
			'player--fullscreen-on': this.props.isFullscreen,
		})

		const controls = (
			<UserActivityListener delay={3} defaultActive={false} onChange={ this.handleIdleChange }>
				<PlayerControls
					minimal={ !this.props.controls }
					handlePlayClick={ this.handleVideoClick }
					handleVideoDoubleClick={ this.handleVideoDoubleClick }
					handleFullscreenClick={ this.handleFullscreenClick }
					handleVolumeUpdate={ this.handleVolumeUpdate }
					onSeek={ this.handleSeek }
					isPlaying={ this.state.isPlaying }
					isBuffering={ this.state.isBuffering }
					isInitiated={ this.state.isInitiated }
					{ ...this.state.videoProps }
				/>
			</UserActivityListener>
		)

		return (
			<div
				ref={ref => this.ref = ref}
				className={classes}
				style={style}
				>
					<video
						style={{ cursor: 'pointer' }}
						preload={ this.props.preload }
						ref={el => this.video = el}
						poster={this.props.poster}
						loop={this.props.loop}
						src={this.props.src}
						width={this.props.width}
						height={this.props.height}
						muted={this.props.muted}
						{ ...this.videoListeners }
					/>
				{ this.props.controls && controls }
   		</div>
		)
	}
}

Player.propTypes = {
	poster: PropTypes.string,
	src: PropTypes.string.isRequired,
	width: PropTypes.number.isRequired,
	height: PropTypes.number.isRequired,
	autoPlay: PropTypes.bool,
	startTime: PropTypes.number,
	controls: PropTypes.bool,
	onItemClick: PropTypes.func,
	style: PropTypes.object,
	loop: PropTypes.bool,

	// from Fullscreen decorator
	toggleFullscreen: PropTypes.func,
	fullscreenAvailable: PropTypes.bool,
	isFullscreen: PropTypes.bool,
}

Player.defaultProps ={
	autoPlay: false,
	startTime: 0,
	controls: true,
	loop: false
}



// WEBPACK FOOTER //
// ./src/components/player/Player.jsx